import React, {useState, ChangeEvent} from 'react'
import {Button} from '@/components/ui/button'
import {useToast} from '@/hooks/useToast'
import useResponsiveWidth from '../../hooks/useResponsiveWidth'
import {useCurrentBasket} from '@/hooks/basket/useCurrentBasket'
import {useAddToCartModalContext} from '~/app/hooks/AddtoCartModal/use-add-to-cart-modal'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useProductContext} from '@/context/ProductContext'
import {ProductDataType} from '@/types/product'
import {useAddItemToBasket} from '@/hooks/basket/useAddItemToBasket'

const MAX_QUANTITY = 150

interface AddToCartProps {
    showStockStatus?: boolean
    isProductTile?: boolean
}

const IncreaseQuantityButton = (props) => <Button {...props} data-testid="addToCart-Button" />
const DecreaseQuantityButton = (props) => <Button {...props} data-testid="addToCart-Button" />

export const AddToCart: React.FC<AddToCartProps> = ({showStockStatus, isProductTile}) => {
    const [isLoading, setIsLoading] = useState(false)
    const {
        product,
        activeProduct,
        productImageSrc,
        isAddToCart,
        addedToCartPodId,
        setQuickviewModalOpen,
        quickviewModalOpen,
        itemQuantity,
        setItemQuantity
    } = useProductContext()

    const [inputValue, setInputValue] = useState<string>(String(itemQuantity))
    const {elementRef, isCompact} = useResponsiveWidth(120)

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {formatMessage} = useIntl()
    const {handleAddItemToBasket} = useAddItemToBasket()

    const {onOpen} = useAddToCartModalContext()
    const {
        data: currentBasketData,
        isLoading: isCurrentBasketDataLoading,
        refetchBaskets
    } = useCurrentBasket()
    const showToast = useToast()
    const isInStock = activeProduct ? activeProduct.inventory?.ats > 0 : product?.inventory?.ats > 0
    const isMaster = product.type?.master
    const isBundle = product.type?.bundle

    let productToAdd: ProductDataType
    if ((isMaster && activeProduct) || (isBundle && activeProduct)) {
        productToAdd = activeProduct
    } else {
        productToAdd = product
    }

    const handleAddToCart = async (
        item: ProductDataType,
        quantity: number,
        addedToCartPodId: string
    ) => {
        setIsLoading(true)
        const productItem = {
            productId: item.id,
            quantity
        }

        try {
            let basketId = currentBasketData?.basketId
            if (!basketId) {
                // Await refetch and use the returned data
                const refetchResult = await refetchBaskets()
                basketId = refetchResult?.data?.baskets?.[0]?.basketId
            }
            await handleAddItemToBasket(productItem, basketId)
            quickviewModalOpen && setQuickviewModalOpen(false)
            onOpen({
                recommendations: {
                    podId: addedToCartPodId
                },
                product: {
                    name: item.name,
                    imageGroups: item.imageGroups,
                    image: productImageSrc,
                    id: item.id
                },
                quantity
            })
        } catch (error) {
            console.error(error)
            showToast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error'
            })
        }

        setIsLoading(false)
    }

    const handleIncrement = () => {
        setItemQuantity((prev) => {
            const newQuantity = Math.min(Number(prev) + 1, MAX_QUANTITY)
            setInputValue(String(newQuantity))
            return newQuantity
        })
    }

    const handleDecrement = () => {
        setItemQuantity((prev) => {
            const newQuantity = Math.max(Number(prev) - 1, 1)
            setInputValue(String(newQuantity))
            return newQuantity
        })
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (value === '' || /^\d+$/.test(value)) {
            setInputValue(value)
        }
    }

    const handleInputBlur = () => {
        const numValue = Number(inputValue)
        if (inputValue === '' || numValue < 1) {
            setInputValue('1')
            setItemQuantity(1)
        } else if (numValue > MAX_QUANTITY) {
            setInputValue(String(MAX_QUANTITY))
            setItemQuantity(MAX_QUANTITY)
        } else {
            setItemQuantity(numValue)
            setInputValue(String(numValue))
        }
    }

    const safeQuantity = itemQuantity && itemQuantity > 0 ? itemQuantity : 1

    return (
        <div>
            <div className="flex xs:gap-2">
                <div className="flex w-1/2 pl-0 pr-[.25rem] border-2 border-solid border-[#283d5d] inline-block rounded-[0.5625rem] mr-[.08rem] sm:mr-[.75rem] ">
                    <DecreaseQuantityButton
                        className="flex-1 self-center pl-[.375rem]"
                        ctaType="link"
                        onClick={handleDecrement}
                        disabled={itemQuantity === 1}
                        data-testid="addToCart-DecreaseQuantityButton"
                    >
                        -
                    </DecreaseQuantityButton>

                    <label className="hidden absolute" htmlFor={`qty-${productToAdd.id}`}>
                        Quantity
                    </label>
                    <input
                        value={inputValue}
                        className="text-center flex-2 sm:w-[calc(100%-3rem)] w-[calc(100%-1rem)] focus:outline-none"
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        inputMode="numeric"
                        min={1}
                        maxLength={3}
                        max={MAX_QUANTITY}
                    />
                    <IncreaseQuantityButton
                        className="flex-1 self-center mt-[.1rem]"
                        ctaType="link"
                        onClick={handleIncrement}
                        disabled={itemQuantity >= MAX_QUANTITY}
                        data-testid="addToCart-IncreaseQuantityButton"
                    >
                        +
                    </IncreaseQuantityButton>
                </div>
                <div className="w-1/2 inline-block">
                    <Button
                        ctaType="button"
                        variant="primary"
                        className="w-full inline-block px-[0.125rem] pt-[.875rem] pb-[.875rem] text-center rounded-[.5rem] leading-tight"
                        onClick={() => {
                            if (isMaster && isProductTile && !quickviewModalOpen) {
                                setItemQuantity(safeQuantity)
                                setQuickviewModalOpen(true)
                                return
                            } else {
                                return handleAddToCart(productToAdd, safeQuantity, addedToCartPodId)
                            }
                        }}
                        disabled={isLoading || !isInStock || isCurrentBasketDataLoading}
                        data-testid="addToCart-Button"
                        ref={elementRef}
                    >
                        {isInStock
                            ? isAddToCart || isCompact
                                ? 'Add'
                                : 'Add To Cart'
                            : 'Out of Stock'}
                    </Button>
                </div>
            </div>
            {showStockStatus && isInStock && (
                <p className="text-sm mt-[.5rem] text-accent">In Stock</p>
            )}
        </div>
    )
}
